import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { OnboardingStateService } from '@app/core/services/onboarding-state.service';
import { CrossPlatformOnboardingStateService } from '@app/core/services/cross-platform-onboarding-state.service';

/**
 * Prevents deep-linking into a wizard step before its prerequisites
 * are met. The guard reads the URL (via `RouterStateSnapshot.url`,
 * which preserves the full path even when applied on a child route)
 * to determine which step is being entered, then queries the in-memory
 * `OnboardingStateService`.
 *
 * On failure, the user is redirected to `/onboarding/account-type` —
 * that page calls `state.reset()` in `ngOnInit`, so the wizard always
 * restarts from a clean slate after a deep-link bounce.
 *
 * Step prerequisite table:
 *   personal/profile          — accountType === 'personal'
 *   personal/source-of-funds  — accountType === 'personal' AND profile saved
 *   corporate/company-info    — accountType === 'corporate'
 *   corporate/business-details — accountType === 'corporate' AND companyInfo saved
 *   corporate/source-of-wealth — accountType === 'corporate' AND companyInfo AND businessDetails saved
 */
export const onboardingStepGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const onboardingState = inject(OnboardingStateService);
  const cpState = inject(CrossPlatformOnboardingStateService);
  const router = inject(Router);

  const url = state.url;
  const redirectToAccountType = () => router.createUrlTree(['/onboarding/account-type']);

  // CW-13h LD-4 / spec §9 — cross-platform mode pass-through.
  // Wizard step-2+ gates in register-mode rely on
  // OnboardingStateService.personalProfileComplete() etc., which the
  // cross-platform branch never populates. Pass through whenever
  // cpState carries a valid registeredPlatform + accountType pair —
  // the wizard pages themselves enforce mode-specific submit rules.
  if (cpState.isCrossPlatform() && cpState.accountType() !== null) {
    return true;
  }

  // Personal flow ---------------------------------------------------
  if (url.startsWith('/onboarding/personal/profile')) {
    return onboardingState.accountType() === 'personal'
      ? true
      : redirectToAccountType();
  }

  if (url.startsWith('/onboarding/personal/source-of-funds')) {
    if (
      onboardingState.accountType() === 'personal' &&
      onboardingState.personalProfileComplete()
    ) {
      return true;
    }
    return redirectToAccountType();
  }

  // Corporate flow --------------------------------------------------
  if (url.startsWith('/onboarding/corporate/company-info')) {
    return onboardingState.accountType() === 'corporate'
      ? true
      : redirectToAccountType();
  }

  if (url.startsWith('/onboarding/corporate/business-details')) {
    if (
      onboardingState.accountType() === 'corporate' &&
      onboardingState.corporateCompanyInfoComplete()
    ) {
      return true;
    }
    return redirectToAccountType();
  }

  if (url.startsWith('/onboarding/corporate/source-of-wealth')) {
    if (
      onboardingState.accountType() === 'corporate' &&
      onboardingState.corporateCompanyInfoComplete() &&
      onboardingState.corporateBusinessDetailsComplete()
    ) {
      return true;
    }
    return redirectToAccountType();
  }

  // Unknown step path — fail closed.
  return redirectToAccountType();
};
